import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import { formatDate, calculateAge } from '../utils/AuthUtils';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import { printDocument, withAutoPrint } from '../utils/printDocument';
import Odontogram from './Odontogram';
import ProgressNoteModal from './ProgressNoteModal';
import DrugLabelPrintModal from './DrugLabelPrintModal';
import {
  User, Phone, Mail, MapPin, AlertTriangle, Heart, Calendar,
  FileText, Smile, Plus, Edit, Eye, X, Save, ChevronDown, ChevronUp,
  Stethoscope, Camera, Image, Trash2, CreditCard, Droplet,
  ClipboardList, MessageSquare, Printer, Tags
} from 'lucide-react';

const PatientRecord = ({ patient, currentBranch, user, onClose, onPatientUpdate }) => {
  const { print_lang } = useLanguage();

  // The dispensed line whose label is being printed, or null.
  const [labelTarget, setLabelTarget] = useState(null);

  // Tab states
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'treatments', 'odontogram', 'notes', 'images'

  // Data states
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [treatmentCatalog, setTreatmentCatalog] = useState([]);

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTreatments, setExpandedTreatments] = useState({});

  // Progress Note Modal
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // Inline error banner for fill-prescription / confirm-sale failures, and
  // the note awaiting an in-page delete confirmation — no native
  // window.alert/confirm anywhere in this component.
  const [noteActionError, setNoteActionError] = useState('');
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null);

  // Treatment Record Modal
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  // Inline error banner for treatment save failures (shown in this modal)
  // and treatment print failures (shown atop the Treatments tab) — no
  // native window.alert for either.
  const [treatmentActionError, setTreatmentActionError] = useState('');
  // A print that SUCCEEDED but not silently. Kept apart from the error state
  // so "printed via the browser" is not dressed up in a red failure banner.
  const [treatmentActionWarning, setTreatmentActionWarning] = useState('');

  // Clinical images (the Images tab). Kept apart from treatmentHistory because
  // they are their own rows and can be deleted individually.
  const [clinicalImages, setClinicalImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [treatmentFormData, setTreatmentFormData] = useState({
    treatment_id: '',
    tooth_number: '',
    dentist_id: '',
    date: new Date().toISOString().split('T')[0],
    diagnosis: '',
    notes: '',
    cost: ''
  });

  // Image upload
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const imageInputRef = useRef(null);

  // Filter states
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  useEffect(() => {
    if (patient?.patient_id) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patient_id]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchTreatmentHistory(),
        fetchProgressNotes(),
        fetchDentists(),
        fetchTreatmentCatalog(),
        fetchClinicalImages()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTreatmentHistory = async () => {
    try {
      const data = await fetchWithAuth(`/api/treatment-records/patient/${patient.patient_id}`);
      setTreatmentHistory(data.data || []);
    } catch (error) {
      console.error('Failed to fetch treatment history:', error);
    }
  };

  const fetchClinicalImages = async () => {
    try {
      const data = await fetchWithAuth(`/api/patients/${patient.patient_id}/images`);
      // The endpoint returns the profile photo and treatment images too; this
      // tab renders those from their own sources already, so take only the
      // rows that belong to this gallery.
      setClinicalImages((data.data || []).filter((i) => i.type === 'clinical'));
    } catch (error) {
      console.error('Failed to fetch clinical images:', error);
      setClinicalImages([]);
    }
  };

  // Upload one or more images against this patient's record.
  //
  // This replaces a handler that only console.log'd the chosen file, which is
  // why the button appeared to do nothing at all.
  const handleUploadImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setImageError('');
    setImageUploading(true);
    try {
      const form = new FormData();
      for (const f of files) form.append('images', f);
      if (currentBranch?.branch_id) form.append('branch_id', currentBranch.branch_id);

      // fetchWithAuth strips Content-Type for FormData so the browser can set
      // its own multipart boundary.
      await fetchWithAuth(`/api/patients/${patient.patient_id}/images`, {
        method: 'POST',
        body: form,
      });
      await fetchClinicalImages();
    } catch (error) {
      const byCode = {
        IMAGE_TOO_LARGE: print_lang('image_too_large', 'Image is larger than 10MB'),
        IMAGE_TYPE_INVALID: print_lang('image_type_invalid', 'Only image files can be uploaded'),
      };
      setImageError(byCode[error.code] || error.message || print_lang('upload_failed', 'Upload failed'));
    } finally {
      setImageUploading(false);
    }
  };

  const handleDeleteImage = async (xrayId) => {
    if (!window.confirm(print_lang('confirm_delete_image', 'Delete this image?'))) return;
    setImageError('');
    try {
      await fetchWithAuth(`/api/patients/${patient.patient_id}/images/${xrayId}`, {
        method: 'DELETE',
      });
      await fetchClinicalImages();
    } catch (error) {
      setImageError(error.message || print_lang('delete_failed', 'Delete failed'));
    }
  };

  const fetchProgressNotes = async () => {
    try {
      const data = await fetchWithAuth(`/api/progress-notes/patient/${patient.patient_id}`);
      setProgressNotes(data.data || []);
    } catch (error) {
      console.error('Failed to fetch progress notes:', error);
      setProgressNotes([]);
    }
  };

  const fetchDentists = async () => {
    try {
      const data = await fetchWithAuth(`/api/appointments/dentists/selection?branch_id=${currentBranch.branch_id}`);
      setDentists(data.data || data || []);
    } catch (error) {
      console.error('Failed to fetch dentists:', error);
    }
  };

  const fetchTreatmentCatalog = async () => {
    try {
      const data = await fetchWithAuth('/api/treatments?status=active');
      setTreatmentCatalog(data.data || data || []);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/${path}`;
  };

  const toggleTreatmentExpand = (tmrId) => {
    setExpandedTreatments(prev => ({
      ...prev,
      [tmrId]: !prev[tmrId]
    }));
  };

  // Progress Note handlers
  const handleAddNote = () => {
    setSelectedNote(null);
    setShowNoteModal(true);
  };

  const handleEditNote = (note) => {
    setSelectedNote(note);
    setShowNoteModal(true);
  };

  const handleFillPrescription = async (prescriptionId) => {
    setNoteActionError('');
    try {
      await fetchWithAuth(`/api/prescriptions/${prescriptionId}/fill`, {
        method: 'PUT', body: JSON.stringify({}),
      });
      fetchProgressNotes();
    } catch (err) {
      // The API names the medication and the shortfall; show that, not a
      // generic failure. No native dialog: the inline banner above the list.
      setNoteActionError(
        err.code === 'INSUFFICIENT_STOCK'
          ? `${print_lang('insufficient_stock', 'Not enough stock')}: ${err.payload?.medication_name} (${err.payload?.available})`
          : (err.message || print_lang('save_failed', 'Save failed'))
      );
    }
  };

  const handleConfirmSale = async (saleId) => {
    setNoteActionError('');
    try {
      await fetchWithAuth(`/api/shop/sales/${saleId}/confirm`, {
        method: 'PUT', body: JSON.stringify({}),
      });
      fetchProgressNotes();
    } catch (err) {
      // The API names the product and the shortfall; show that, not a
      // generic failure. No native dialog: the inline banner above the list.
      setNoteActionError(
        err.code === 'INSUFFICIENT_STOCK'
          ? `${print_lang('insufficient_stock', 'Not enough stock')}: ${err.payload?.product_name} (${err.payload?.available})`
          : (err.message || print_lang('save_failed', 'Save failed'))
      );
    }
  };

  // Delete is a two-step, in-page confirmation — no window.confirm.
  const requestDeleteNote = (noteId) => setConfirmDeleteNote(noteId);

  const handleDeleteNote = async () => {
    const noteId = confirmDeleteNote;
    setConfirmDeleteNote(null);
    if (!noteId) return;
    try {
      await fetchWithAuth(`/api/progress-notes/${noteId}`, { method: 'DELETE' });
      fetchProgressNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  // Treatment handlers
  const handleAddTreatment = () => {
    setTreatmentFormData({
      treatment_id: '',
      tooth_number: '',
      dentist_id: user?.dentist_id || '',
      date: new Date().toISOString().split('T')[0],
      diagnosis: '',
      notes: '',
      cost: ''
    });
    setTreatmentActionError('');
    setShowTreatmentModal(true);
  };

  const handleSaveTreatment = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setTreatmentActionError('');
    try {
      await fetchWithAuth('/api/treatment-records', {
        method: 'POST',
        body: JSON.stringify({
          ...treatmentFormData,
          patient_id: patient.patient_id,
          branch_id: currentBranch.branch_id
        })
      });
      setShowTreatmentModal(false);
      fetchTreatmentHistory();
    } catch (error) {
      console.error('Failed to save treatment:', error);
      // Prefer the translated string; error.message (the server's raw text)
      // is only the fallback if no translation exists — same idiom as
      // DrugLabelPrintModal's print_failed handling. No native dialog: the
      // banner rendered inside this modal, below.
      setTreatmentActionError(print_lang('save_failed', error.message || 'Save failed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Group treatments by date
  const groupedTreatments = treatmentHistory.reduce((groups, treatment) => {
    const date = treatment.date ? treatment.date.split('T')[0] : 'unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(treatment);
    return groups;
  }, {});

  // Filter treatments
  const filteredGroupedTreatments = Object.entries(groupedTreatments).filter(([date]) => {
    if (dateFilter.from && date < dateFilter.from) return false;
    if (dateFilter.to && date > dateFilter.to) return false;
    return true;
  });

  // Print single treatment record
  const handlePrintSingleTreatment = async (treatment) => {
    setTreatmentActionError('');
    setTreatmentActionWarning('');

    const printContent = `
      <html>
        <head>
          <title>Treatment Record - ${treatment.treatment_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; font-size: 12px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
            .header h1 { color: #1e40af; margin: 0 0 10px 0; font-size: 24px; }
            .header .subtitle { color: #6b7280; font-size: 14px; }
            .patient-info { display: flex; justify-content: space-around; background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .patient-info div { text-align: center; }
            .patient-info .label { font-size: 10px; color: #6b7280; text-transform: uppercase; }
            .patient-info .value { font-size: 14px; font-weight: bold; color: #1f2937; }
            .treatment-card { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 20px; }
            .treatment-header { background: linear-gradient(135deg, #2563eb, #3b82f6); color: white; padding: 15px; }
            .treatment-header h2 { margin: 0 0 5px 0; font-size: 18px; }
            .treatment-header .date { font-size: 12px; opacity: 0.9; }
            .treatment-body { padding: 20px; }
            .details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 20px; }
            .detail-item { padding: 10px; background: #f9fafb; border-radius: 6px; border-left: 4px solid #3b82f6; }
            .detail-item .label { font-size: 10px; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
            .detail-item .value { font-size: 14px; font-weight: 600; color: #1f2937; }
            .notes-section { background: #fefce8; border: 1px solid #fde047; border-radius: 6px; padding: 15px; margin-bottom: 20px; }
            .notes-section h3 { margin: 0 0 10px 0; font-size: 14px; color: #854d0e; }
            .notes-section p { margin: 0; line-height: 1.6; color: #713f12; white-space: pre-wrap; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
            .status-completed { background: #dcfce7; color: #166534; }
            .status-pending { background: #fef3c7; color: #92400e; }
            .footer { margin-top: 30px; padding-top: 15px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 10px; color: #9ca3af; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🦷 ${print_lang('treatment_record_details', 'Treatment Record Details')}</h1>
            <div class="subtitle">${print_lang('printed_on', 'Printed on')}: ${new Date().toLocaleString()}</div>
          </div>

          <div class="patient-info">
            <div>
              <div class="label">${print_lang('patient_name', 'Patient Name')}</div>
              <div class="value">${patient?.name || '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('citizen_id', 'Citizen ID')}</div>
              <div class="value">${patient?.citizen_id || '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('age', 'Age')}</div>
              <div class="value">${patient?.dob ? calculateAge(patient.dob) + ' ' + print_lang('years', 'years') : '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('phone', 'Phone')}</div>
              <div class="value">${patient?.phone || '-'}</div>
            </div>
          </div>

          <div class="treatment-card">
            <div class="treatment-header">
              <h2>${treatment.treatment_name || print_lang('unnamed_treatment', 'Unnamed Treatment')}</h2>
              <div class="date">${formatDate(treatment.date)}</div>
            </div>
            <div class="treatment-body">
              <div class="details-grid">
                <div class="detail-item">
                  <div class="label">${print_lang('treatment_id', 'Treatment ID')}</div>
                  <div class="value">${treatment.tmr_id || '-'}</div>
                </div>
                <div class="detail-item">
                  <div class="label">${print_lang('status', 'Status')}</div>
                  <div class="value"><span class="status-badge status-${treatment.status}">${print_lang(treatment.status, treatment.status)}</span></div>
                </div>
                <div class="detail-item">
                  <div class="label">${print_lang('dentist', 'Dentist')}</div>
                  <div class="value">${treatment.dentist_name || '-'}</div>
                </div>
                <div class="detail-item">
                  <div class="label">${print_lang('tooth_number', 'Tooth Number')}</div>
                  <div class="value">${treatment.tooth_number || '-'}</div>
                </div>
                <div class="detail-item">
                  <div class="label">${print_lang('cost', 'Cost')}</div>
                  <div class="value">${treatment.cost?.toLocaleString() || 0} ฿</div>
                </div>
                <div class="detail-item">
                  <div class="label">${print_lang('category', 'Category')}</div>
                  <div class="value">${treatment.category || '-'}</div>
                </div>
              </div>

              ${treatment.diagnosis ? `
                <div class="notes-section">
                  <h3>📋 ${print_lang('diagnosis', 'Diagnosis')}</h3>
                  <p>${treatment.diagnosis}</p>
                </div>
              ` : ''}

              ${treatment.notes ? `
                <div class="notes-section">
                  <h3>📝 ${print_lang('notes', 'Notes')}</h3>
                  <p>${treatment.notes}</p>
                </div>
              ` : ''}
            </div>
          </div>

          <div class="footer">
            <p>${currentBranch?.name || 'Dental Clinic'} - ${print_lang('generated_by_dental_clinic', 'Generated by Dental Clinic Management System')}</p>
          </div>
        </body>
      </html>
    `;
    // A4, so it goes to the branch's `document` printer when the agent is
    // running, and to the browser print dialog when it is not.
    const result = await printDocument({
      agentHtml: printContent,
      browserHtml: withAutoPrint(printContent),
      printerType: 'document',
      branch: currentBranch,
    });
    if (result.popupBlocked) {
      setTreatmentActionError(print_lang('allow_popup_for_print', 'Please allow pop-ups for printing'));
      return;
    }
    if (result.warned) {
      setTreatmentActionWarning(
        print_lang('printed_via_browser', 'Print agent offline — printed via the browser'));
    }
  };

  // Print all treatment records for patient
  const handlePrintAllTreatments = async () => {
    if (treatmentHistory.length === 0) return;

    setTreatmentActionError('');
    setTreatmentActionWarning('');

    const totalCost = treatmentHistory.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);

    const printContent = `
      <html>
        <head>
          <title>Treatment Records - ${patient?.name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; font-size: 12px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
            .header h1 { color: #1e40af; margin: 0 0 10px 0; font-size: 24px; }
            .header h2 { color: #374151; margin: 0 0 5px 0; font-size: 18px; }
            .header .subtitle { color: #6b7280; font-size: 12px; }
            .patient-info { display: flex; justify-content: space-around; background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 25px; }
            .patient-info div { text-align: center; }
            .patient-info .label { font-size: 10px; color: #6b7280; text-transform: uppercase; }
            .patient-info .value { font-size: 14px; font-weight: bold; color: #1f2937; }
            .treatment-record { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 15px; overflow: hidden; page-break-inside: avoid; }
            .treatment-header { background: linear-gradient(135deg, #2563eb, #3b82f6); color: white; padding: 12px 15px; display: flex; justify-content: space-between; align-items: center; }
            .treatment-header .name { font-size: 14px; font-weight: bold; }
            .treatment-header .date { font-size: 11px; opacity: 0.9; }
            .treatment-body { padding: 12px 15px; }
            .details-row { display: flex; flex-wrap: wrap; gap: 10px; }
            .detail-item { flex: 1; min-width: 100px; padding: 6px 10px; background: #f9fafb; border-radius: 4px; border-left: 3px solid #3b82f6; }
            .detail-item .label { font-size: 9px; color: #6b7280; text-transform: uppercase; }
            .detail-item .value { font-size: 11px; font-weight: 600; color: #1f2937; }
            .notes-box { background: #fefce8; border: 1px solid #fde047; border-radius: 4px; padding: 8px; margin-top: 10px; font-size: 11px; }
            .status-badge { display: inline-block; padding: 3px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
            .status-completed { background: #dcfce7; color: #166534; }
            .status-pending { background: #fef3c7; color: #92400e; }
            .summary { margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #eff6ff, #f5f3ff); border-radius: 8px; border: 1px solid #c7d2fe; }
            .summary h3 { margin: 0 0 15px 0; color: #3730a3; font-size: 14px; }
            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center; }
            .summary-item .number { font-size: 24px; font-weight: bold; }
            .summary-item .label { font-size: 10px; color: #6b7280; text-transform: uppercase; }
            .footer { margin-top: 30px; padding-top: 15px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 10px; color: #9ca3af; }
            @media print { body { padding: 10px; } .treatment-record { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🦷 ${print_lang('treatment_records_report', 'Treatment Records Report')}</h1>
            <h2>${patient?.name || print_lang('patient', 'Patient')}</h2>
            <div class="subtitle">${print_lang('printed_on', 'Printed on')}: ${new Date().toLocaleString()}</div>
          </div>

          <div class="patient-info">
            <div>
              <div class="label">${print_lang('patient_name', 'Patient Name')}</div>
              <div class="value">${patient?.name || '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('citizen_id', 'Citizen ID')}</div>
              <div class="value">${patient?.citizen_id || '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('age', 'Age')}</div>
              <div class="value">${patient?.dob ? calculateAge(patient.dob) + ' ' + print_lang('years', 'years') : '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('phone', 'Phone')}</div>
              <div class="value">${patient?.phone || '-'}</div>
            </div>
            <div>
              <div class="label">${print_lang('total_records', 'Total Records')}</div>
              <div class="value">${treatmentHistory.length}</div>
            </div>
          </div>

          ${treatmentHistory.map(treatment => `
            <div class="treatment-record">
              <div class="treatment-header">
                <div>
                  <div class="name">${treatment.treatment_name || print_lang('unnamed_treatment', 'Unnamed Treatment')}</div>
                  <div class="date">${formatDate(treatment.date)}${treatment.tooth_number ? ' • Tooth #' + treatment.tooth_number : ''}</div>
                </div>
                <span class="status-badge status-${treatment.status}">${print_lang(treatment.status, treatment.status)}</span>
              </div>
              <div class="treatment-body">
                <div class="details-row">
                  <div class="detail-item">
                    <div class="label">${print_lang('dentist', 'Dentist')}</div>
                    <div class="value">${treatment.dentist_name || '-'}</div>
                  </div>
                  <div class="detail-item">
                    <div class="label">${print_lang('cost', 'Cost')}</div>
                    <div class="value">${treatment.cost?.toLocaleString() || 0} ฿</div>
                  </div>
                  <div class="detail-item">
                    <div class="label">${print_lang('category', 'Category')}</div>
                    <div class="value">${treatment.category || '-'}</div>
                  </div>
                </div>
                ${treatment.diagnosis || treatment.notes ? `
                  <div class="notes-box">
                    ${treatment.diagnosis ? `<strong>${print_lang('diagnosis', 'Diagnosis')}:</strong> ${treatment.diagnosis}<br/>` : ''}
                    ${treatment.notes ? `<strong>${print_lang('notes', 'Notes')}:</strong> ${treatment.notes}` : ''}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('')}

          <div class="summary">
            <h3>📊 ${print_lang('treatment_summary', 'Treatment Summary')}</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="number" style="color: #2563eb;">${treatmentHistory.length}</div>
                <div class="label">${print_lang('total_treatments', 'Total Treatments')}</div>
              </div>
              <div class="summary-item">
                <div class="number" style="color: #22c55e;">${treatmentHistory.filter(t => t.status === 'completed').length}</div>
                <div class="label">${print_lang('completed', 'Completed')}</div>
              </div>
              <div class="summary-item">
                <div class="number" style="color: #eab308;">${treatmentHistory.filter(t => t.status === 'pending').length}</div>
                <div class="label">${print_lang('pending', 'Pending')}</div>
              </div>
              <div class="summary-item">
                <div class="number" style="color: #a855f7;">${totalCost.toLocaleString()} ฿</div>
                <div class="label">${print_lang('total_cost', 'Total Cost')}</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>${currentBranch?.name || 'Dental Clinic'} - ${print_lang('generated_by_dental_clinic', 'Generated by Dental Clinic Management System')}</p>
          </div>
        </body>
      </html>
    `;
    // A4, so it goes to the branch's `document` printer when the agent is
    // running, and to the browser print dialog when it is not.
    const result = await printDocument({
      agentHtml: printContent,
      browserHtml: withAutoPrint(printContent),
      printerType: 'document',
      branch: currentBranch,
    });
    if (result.popupBlocked) {
      setTreatmentActionError(print_lang('allow_popup_for_print', 'Please allow pop-ups for printing'));
      return;
    }
    if (result.warned) {
      setTreatmentActionWarning(
        print_lang('printed_via_browser', 'Print agent offline — printed via the browser'));
    }
  };

  // Tabs
  const tabs = [
    { id: 'overview', label: 'overview', labelEn: 'Overview', icon: User },
    { id: 'treatments', label: 'treatments', labelEn: 'Treatments', icon: Stethoscope },
    { id: 'odontogram', label: 'odontogram', labelEn: 'Odontogram', icon: Smile },
    { id: 'notes', label: 'progress_notes', labelEn: 'Progress Notes', icon: ClipboardList },
    { id: 'images', label: 'images', labelEn: 'Images', icon: Image }
  ];

  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Patient Info Card */}
      <div className="lg:col-span-2 space-y-6">
        {/* Basic Info */}
        <div className="card">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <User size={20} />
            {print_lang('patient_info', 'Patient Information')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500">{print_lang('citizen_id', 'Citizen ID')}</label>
              <p className="font-medium flex items-center gap-2">
                <CreditCard size={16} className="text-gray-400" />
                {patient.citizen_id || '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{print_lang('dob', 'Date of Birth')}</label>
              <p className="font-medium">
                {patient.dob ? `${formatDate(patient.dob)} (${calculateAge(patient.dob)} ${print_lang('years', 'years')})` : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{print_lang('gender', 'Gender')}</label>
              <p className="font-medium">{print_lang(patient.gender, patient.gender)}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{print_lang('blood_group', 'Blood Group')}</label>
              <p className="font-medium flex items-center gap-2">
                <Droplet size={16} className="text-red-400" />
                {patient.blood_group || '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{print_lang('phone', 'Phone')}</label>
              <p className="font-medium flex items-center gap-2">
                <Phone size={16} className="text-gray-400" />
                {patient.phone || '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{print_lang('email', 'Email')}</label>
              <p className="font-medium flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                {patient.email || '-'}
              </p>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-gray-500">{print_lang('address', 'Address')}</label>
              <p className="font-medium flex items-center gap-2">
                <MapPin size={16} className="text-gray-400" />
                {patient.address || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Medical History */}
        <div className="card">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Heart size={20} className="text-red-500" />
            {print_lang('medical_info', 'Medical Information')}
          </h3>

          {/* Drug Allergy Warning */}
          {patient.drug_allergy && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-red-700">{print_lang('drug_allergy', 'Drug Allergy')}</p>
                  <p className="text-red-600">{patient.drug_allergy}</p>
                </div>
              </div>
            </div>
          )}

          {/* Medical History */}
          {patient.medical_history && (
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4 rounded">
              <div className="flex items-start gap-3">
                <Heart className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-orange-700">{print_lang('medical_history', 'Medical History')}</p>
                  <p className="text-orange-600 whitespace-pre-line">{patient.medical_history}</p>
                </div>
              </div>
            </div>
          )}

          {!patient.drug_allergy && !patient.medical_history && (
            <p className="text-gray-400 text-center py-4">{print_lang('no_medical_info', 'No medical information recorded')}</p>
          )}

          {/* Emergency Contact */}
          {(patient.emergency_contact || patient.emergency_phone) && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mt-4">
              <p className="font-bold text-blue-700 mb-1">{print_lang('emergency_contact', 'Emergency Contact')}</p>
              <p className="text-blue-600">{patient.emergency_contact} - {patient.emergency_phone}</p>
            </div>
          )}
        </div>

        {/* Recent Treatments */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Stethoscope size={20} />
              {print_lang('recent_treatments', 'Recent Treatments')}
            </h3>
            <button
              onClick={() => setActiveTab('treatments')}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              {print_lang('view_all', 'View All')} →
            </button>
          </div>

          {treatmentHistory.length === 0 ? (
            <p className="text-gray-400 text-center py-4">{print_lang('no_treatments', 'No treatment records')}</p>
          ) : (
            <div className="space-y-3">
              {treatmentHistory.slice(0, 5).map((treatment) => (
                <div key={treatment.tmr_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{treatment.treatment_name}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(treatment.date)} • {treatment.dentist_name || '-'}
                      {treatment.tooth_number && ` • #${treatment.tooth_number}`}
                    </p>
                  </div>
                  <span className={`badge ${treatment.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                    {print_lang(treatment.status, treatment.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="space-y-6">
        {/* Patient Photo */}
        <div className="card">
          <div className="flex flex-col items-center">
            {patient.images_path ? (
              <img
                src={getImageUrl(patient.images_path)}
                alt={patient.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-blue-100"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                <User size={48} className="text-gray-400" />
              </div>
            )}
            <h2 className="text-xl font-bold mt-4">
              {patient.prefix} {patient.name}
              {patient.dn && (
                <span className="ml-3 font-mono text-sm text-gray-500">{patient.dn}</span>
              )}
            </h2>
            {patient.name_en && (
              <p className="text-gray-500">{patient.name_en}</p>
            )}
            <span className={`mt-2 badge ${patient.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
              {print_lang(patient.status, patient.status)}
            </span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="card">
          <h3 className="text-lg font-bold mb-4">{print_lang('statistics', 'Statistics')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-gray-600">{print_lang('total_visits', 'Total Visits')}</span>
              <span className="font-bold text-blue-600">{treatmentHistory.length}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-gray-600">{print_lang('last_visit', 'Last Visit')}</span>
              <span className="font-bold text-green-600">
                {patient.last_visit ? formatDate(patient.last_visit) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-gray-600">{print_lang('progress_notes', 'Progress Notes')}</span>
              <span className="font-bold text-purple-600">{progressNotes.length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3 className="text-lg font-bold mb-4">{print_lang('quick_actions', 'Quick Actions')}</h3>
          <div className="space-y-2">
            <button
              onClick={handleAddNote}
              className="w-full btn btn-primary flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              {print_lang('add_progress_note', 'Add Progress Note')}
            </button>
            <button
              onClick={handleAddTreatment}
              className="w-full btn btn-secondary flex items-center justify-center gap-2"
            >
              <Stethoscope size={18} />
              {print_lang('add_treatment', 'Add Treatment')}
            </button>
            <button
              onClick={() => setActiveTab('odontogram')}
              className="w-full btn btn-secondary flex items-center justify-center gap-2"
            >
              <Smile size={18} />
              {print_lang('view_odontogram', 'View Odontogram')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTreatments = () => (
    <div>
      {/* Print failures surface here — no native window.alert. (A save
          failure from the Add Treatment modal shows in that modal instead,
          since the modal can be open regardless of which tab sits behind
          it.) */}
      {treatmentActionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
          {treatmentActionError}
        </div>
      )}
      {treatmentActionWarning && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded mb-4">
          {treatmentActionWarning}
        </div>
      )}

      {/* Filter Bar */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">{print_lang('from_date', 'From')}</label>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{print_lang('to_date', 'To')}</label>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
              className="w-40"
            />
          </div>
          <button
            onClick={() => setDateFilter({ from: '', to: '' })}
            className="btn btn-secondary"
          >
            {print_lang('clear', 'Clear')}
          </button>
          <div className="flex-1"></div>
          <button
            onClick={handlePrintAllTreatments}
            disabled={treatmentHistory.length === 0}
            className={`btn flex items-center gap-2 ${
              treatmentHistory.length > 0
                ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
          >
            <Printer size={18} />
            {print_lang('print_all', 'Print All')}
          </button>
          <button onClick={handleAddTreatment} className="btn btn-primary flex items-center gap-2">
            <Plus size={18} />
            {print_lang('add_treatment', 'Add Treatment')}
          </button>
        </div>
      </div>

      {/* Treatment Timeline */}
      <div className="space-y-6">
        {filteredGroupedTreatments.length === 0 ? (
          <div className="card text-center py-12 text-gray-500">
            <Stethoscope size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{print_lang('no_treatments', 'No treatment records found')}</p>
          </div>
        ) : (
          filteredGroupedTreatments.sort((a, b) => b[0].localeCompare(a[0])).map(([date, treatments]) => (
            <div key={date} className="card">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar size={24} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-lg">{formatDate(date)}</p>
                  <p className="text-sm text-gray-500">{treatments.length} {print_lang('treatments', 'treatments')}</p>
                </div>
              </div>

              <div className="space-y-3">
                {treatments.map((treatment) => (
                  <div key={treatment.tmr_id} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleTreatmentExpand(treatment.tmr_id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Stethoscope size={20} className="text-green-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{treatment.treatment_name}</p>
                          <p className="text-sm text-gray-500">
                            {treatment.dentist_name}
                            {treatment.tooth_number && ` • Tooth #${treatment.tooth_number}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`badge ${treatment.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                          {print_lang(treatment.status, treatment.status)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintSingleTreatment(treatment);
                          }}
                          className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                          title={print_lang('print_record', 'Print Record')}
                        >
                          <Printer size={18} />
                        </button>
                        {expandedTreatments[treatment.tmr_id] ? (
                          <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {expandedTreatments[treatment.tmr_id] && (
                      <div className="px-4 pb-4 border-t bg-gray-50">
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          {treatment.diagnosis && (
                            <div>
                              <label className="text-sm text-gray-500">{print_lang('diagnosis', 'Diagnosis')}</label>
                              <p className="font-medium">{treatment.diagnosis}</p>
                            </div>
                          )}
                          {treatment.notes && (
                            <div>
                              <label className="text-sm text-gray-500">{print_lang('notes', 'Notes')}</label>
                              <p className="font-medium">{treatment.notes}</p>
                            </div>
                          )}
                          <div>
                            <label className="text-sm text-gray-500">{print_lang('cost', 'Cost')}</label>
                            <p className="font-medium">{treatment.cost?.toLocaleString() || 0} ฿</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500">{print_lang('category', 'Category')}</label>
                            <p className="font-medium">{treatment.category || '-'}</p>
                          </div>
                        </div>

                        {/* Treatment Images */}
                        {treatment.images && (
                          <div className="mt-4">
                            <label className="text-sm text-gray-500 block mb-2">{print_lang('images', 'Images')}</label>
                            <div className="flex gap-2 flex-wrap">
                              {treatment.images.split(',').filter(Boolean).map((img, idx) => (
                                <img
                                  key={idx}
                                  src={getImageUrl(img)}
                                  alt={`Treatment ${idx + 1}`}
                                  className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    setSelectedImage(getImageUrl(img));
                                    setShowImageModal(true);
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderOdontogram = () => (
    <div>
      <Odontogram
        patientId={patient.patient_id}
        currentBranch={currentBranch}
      />
    </div>
  );

  const renderProgressNotes = () => (
    <div>
      {/* Add Note Button */}
      <div className="flex justify-end mb-6">
        <button onClick={handleAddNote} className="btn btn-primary flex items-center gap-2">
          <Plus size={18} />
          {print_lang('add_progress_note', 'Add Progress Note')}
        </button>
      </div>

      {/* Fill Prescription / Confirm Sale errors surface here — no native
          window.alert anywhere in this component. */}
      {noteActionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
          {noteActionError}
        </div>
      )}

      {/* Notes List */}
      {progressNotes.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          <ClipboardList size={48} className="mx-auto mb-4 text-gray-300" />
          <p>{print_lang('no_progress_notes', 'No progress notes found')}</p>
          <button onClick={handleAddNote} className="mt-4 text-blue-600 hover:text-blue-800">
            {print_lang('add_first_note', 'Add first progress note')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {progressNotes.map((note) => (
            <div key={note.note_id} className="card">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <MessageSquare size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-bold">{formatDate(note.visit_date)}</p>
                    <p className="text-sm text-gray-500">{note.dentist_name || 'Unknown'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditNote(note)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => requestDeleteNote(note.note_id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {note.chief_complaint && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('chief_complaint', 'Chief Complaint')}</label>
                    <p className="mt-1">{note.chief_complaint}</p>
                  </div>
                )}
                {note.clinical_findings && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('clinical_findings', 'Clinical Findings')}</label>
                    <p className="mt-1">{note.clinical_findings}</p>
                  </div>
                )}
                {note.diagnosis && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('diagnosis', 'Diagnosis')}</label>
                    <p className="mt-1">{note.diagnosis}</p>
                  </div>
                )}
                {note.treatment_performed && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('treatment_performed', 'Treatment Performed')}</label>
                    <p className="mt-1">{note.treatment_performed}</p>
                  </div>
                )}
                {note.medications && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('medications_prescribed', 'Medications')}</label>
                    <p className="mt-1">{note.medications}</p>
                  </div>
                )}
                {note.next_appointment && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">{print_lang('next_appointment', 'Next Appointment')}</label>
                    <p className="mt-1">{formatDate(note.next_appointment)}</p>
                  </div>
                )}
              </div>

              {note.notes && (
                <div className="mt-4 pt-4 border-t">
                  <label className="text-sm font-medium text-gray-500">{print_lang('additional_notes', 'Additional Notes')}</label>
                  <p className="mt-1 whitespace-pre-line">{note.notes}</p>
                </div>
              )}

              {note.prescription && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">
                      {print_lang('medications_prescribed', 'Medications Prescribed')}
                      <span className="ml-2 text-sm text-gray-500">
                        {note.prescription.prescription_number}
                      </span>
                    </span>
                    <span className="badge">
                      {print_lang(`status_${note.prescription.status}`, note.prescription.status)}
                    </span>
                  </div>
                  {(note.prescription.items || []).map((it) => (
                    <div key={it.item_id} className="flex justify-between items-center text-sm py-1">
                      <span>
                        {it.medication_name}{it.strength ? ` ${it.strength}` : ''}
                        {' × '}
                        {Number(it.quantity_filled) || Number(it.quantity_prescribed)}
                      </span>
                      {/* Only a DISPENSED line offers a label. A label for medicine
                          that has not been handed over is a label for medicine the
                          patient does not have — and the endpoint refuses it with
                          409 NOT_DISPENSED anyway, so the button would be a dead
                          end. A cancelled line is excluded for the same reason
                          (409 RX_CANCELLED): the medicine went back to stock.
                          The test is quantity_filled, NOT status === 'filled':
                          a partial fill leaves the row 'pending' while medicine
                          HAS been handed over, and that bag needs its label. This
                          mirrors the endpoint's own condition exactly, so every
                          button shown is a button that works. */}
                      {Number(it.quantity_filled) > 0
                        && it.status !== 'cancelled'
                        && note.prescription.status !== 'cancelled' && (
                        <button className="text-blue-600 flex items-center gap-1"
                                onClick={() => setLabelTarget({
                                  prescriptionId: note.prescription.prescription_id, item: it,
                                })}>
                          <Tags size={14} /> {print_lang('print_drug_label', 'Print Drug Label')}
                        </button>
                      )}
                    </div>
                  ))}
                  {/* 'partially_filled' belongs here as much as 'pending': the
                      fill endpoint computes that status itself when some lines
                      are still outstanding, and it accepts a later call to
                      dispense the rest (it refuses only a cancelled or an
                      already-invoiced prescription). Gating on 'pending' alone
                      stranded the remainder — dispense 5 of 10 and the other 5
                      could never be handed over from this screen. Same root
                      cause as the Print Label gate below: a partial fill is a
                      real, ongoing state, not a synonym for "done". */}
                  {['pending', 'partially_filled'].includes(note.prescription.status) && (
                    <button className="btn btn-primary btn-sm"
                            onClick={() => handleFillPrescription(note.prescription.prescription_id)}>
                      {print_lang('fill_prescription', 'Fill Prescription')}
                    </button>
                  )}
                </div>
              )}

              {note.product_sale && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">
                      {print_lang('products_sold', 'Products Sold')}
                      <span className="ml-2 text-sm text-gray-500">
                        {note.product_sale.sale_number}
                      </span>
                    </span>
                    <span className="badge">
                      {print_lang(`status_${note.product_sale.status}`, note.product_sale.status)}
                    </span>
                  </div>
                  {note.product_sale.status === 'pending' && (
                    <button className="btn btn-primary btn-sm"
                            onClick={() => handleConfirmSale(note.product_sale.sale_id)}>
                      {print_lang('confirm_sale', 'Confirm Sale')}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderImages = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold">{print_lang('patient_images', 'Patient Images')}</h3>
        <button
          onClick={() => imageInputRef.current?.click()}
          disabled={imageUploading}
          className="btn btn-primary flex items-center gap-2"
        >
          <Camera size={18} />
          {imageUploading
            ? print_lang('uploading', 'Uploading...')
            : print_lang('upload_image', 'Upload Image')}
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleUploadImages(e.target.files);
            // Clear it, so picking the SAME file again still fires onChange.
            e.target.value = '';
          }}
        />
      </div>

      {imageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
          {imageError}
        </div>
      )}

      {/* Clinical Images — what this tab's upload button actually adds. */}
      <div className="card mb-6">
        <h4 className="font-bold mb-4">{print_lang('clinical_images', 'Clinical Images')}</h4>
        {clinicalImages.length === 0 ? (
          <p className="text-gray-400 text-center py-8">
            {print_lang('no_clinical_images', 'No clinical images yet')}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {clinicalImages.map((img) => (
              <div key={img.xray_id} className="relative group">
                <img
                  src={getImageUrl(img.path)}
                  alt={img.description || 'Clinical image'}
                  className="w-full h-32 object-cover rounded-lg border cursor-pointer"
                  onClick={() => {
                    setSelectedImage(getImageUrl(img.path));
                    setShowImageModal(true);
                  }}
                />
                <button
                  onClick={() => handleDeleteImage(img.xray_id)}
                  title={print_lang('delete_image', 'Delete image')}
                  className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={16} />
                </button>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {img.date ? formatDate(img.date) : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Treatment Images Gallery */}
      <div className="card">
        <h4 className="font-bold mb-4">{print_lang('treatment_images', 'Treatment Images')}</h4>
        {treatmentHistory.filter(t => t.images).length === 0 ? (
          <p className="text-gray-400 text-center py-8">{print_lang('no_images', 'No images found')}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {treatmentHistory.filter(t => t.images).flatMap((treatment) =>
              treatment.images.split(',').filter(Boolean).map((img, idx) => (
                <div
                  key={`${treatment.tmr_id}-${idx}`}
                  className="relative group cursor-pointer"
                  onClick={() => {
                    setSelectedImage(getImageUrl(img));
                    setShowImageModal(true);
                  }}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Treatment ${idx + 1}`}
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                    <Eye size={24} className="text-white opacity-0 group-hover:opacity-100" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {formatDate(treatment.date)}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="btn btn-secondary">
            ← {print_lang('back', 'Back')}
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText size={28} />
            {print_lang('clinical_record', 'Clinical Record')}
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b overflow-x-auto pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <Icon size={18} />
              {print_lang(tab.label, tab.labelEn)}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'treatments' && renderTreatments()}
      {activeTab === 'odontogram' && renderOdontogram()}
      {activeTab === 'notes' && renderProgressNotes()}
      {activeTab === 'images' && renderImages()}

      {/* Progress Note Modal */}
      <ProgressNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSaved={() => { setShowNoteModal(false); fetchProgressNotes(); }}
        patient={patient}
        dentists={dentists}
        branchId={currentBranch?.branch_id}
        branchVatRate={branchRate(currentBranch)}
        note={selectedNote}
        defaultDentistId={user?.dentist_id}
      />

      {/* Drug Label Print Modal */}
      <DrugLabelPrintModal
        isOpen={!!labelTarget}
        onClose={() => setLabelTarget(null)}
        prescriptionId={labelTarget?.prescriptionId}
        item={labelTarget?.item}
        currentBranch={currentBranch}
      />

      {/* Treatment Modal */}
      {showTreatmentModal && (
        <div className="modal-overlay" onClick={() => setShowTreatmentModal(false)}>
          <div className="modal-content w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Stethoscope size={24} />
                {print_lang('add_treatment', 'Add Treatment Record')}
              </h2>
              <button onClick={() => setShowTreatmentModal(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Save failures surface here — no native window.alert. This
                modal renders regardless of the active tab, so the banner is
                reachable no matter which tab was behind it when opened. */}
            {treatmentActionError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {treatmentActionError}
              </div>
            )}

            <form onSubmit={handleSaveTreatment} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('treatment', 'Treatment')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={treatmentFormData.treatment_id}
                    onChange={(e) => {
                      const treatment = treatmentCatalog.find(t => t.t_id === parseInt(e.target.value));
                      setTreatmentFormData({
                        ...treatmentFormData,
                        treatment_id: e.target.value,
                        cost: treatment?.price || ''
                      });
                    }}
                    required
                  >
                    <option value="">{print_lang('select_treatment', 'Select Treatment')}</option>
                    {treatmentCatalog.map((treatment) => (
                      <option key={treatment.t_id} value={treatment.t_id}>
                        {treatment.name} - {treatment.price?.toLocaleString()}฿
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('dentist', 'Dentist')}
                  </label>
                  <select
                    value={treatmentFormData.dentist_id}
                    onChange={(e) => setTreatmentFormData({ ...treatmentFormData, dentist_id: e.target.value })}
                  >
                    <option value="">{print_lang('select_dentist', 'Select Dentist')}</option>
                    {dentists.map((dentist) => (
                      <option key={dentist.dentist_id} value={dentist.dentist_id}>
                        {dentist.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('tooth_number', 'Tooth Number')}
                  </label>
                  <input
                    type="text"
                    value={treatmentFormData.tooth_number}
                    onChange={(e) => setTreatmentFormData({ ...treatmentFormData, tooth_number: e.target.value })}
                    placeholder="e.g., 11, 21, 36"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('date', 'Date')}
                  </label>
                  <input
                    type="date"
                    value={treatmentFormData.date}
                    onChange={(e) => setTreatmentFormData({ ...treatmentFormData, date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {print_lang('diagnosis', 'Diagnosis')}
                </label>
                <textarea
                  value={treatmentFormData.diagnosis}
                  onChange={(e) => setTreatmentFormData({ ...treatmentFormData, diagnosis: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {print_lang('notes', 'Notes')}
                </label>
                <textarea
                  value={treatmentFormData.notes}
                  onChange={(e) => setTreatmentFormData({ ...treatmentFormData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {print_lang('cost', 'Cost')}
                </label>
                <input
                  type="number"
                  value={treatmentFormData.cost}
                  onChange={(e) => setTreatmentFormData({ ...treatmentFormData, cost: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowTreatmentModal(false)}
                  className="btn btn-secondary"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Save size={18} />
                  {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="max-w-4xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg"
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Delete Progress Note confirmation — in-page, no window.confirm.
          A click outside the box cancels, same as the other modals above. */}
      {confirmDeleteNote && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteNote(null)}>
          <div className="modal-content w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">
              {print_lang('confirm_delete', 'Delete this item?')}
            </h3>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setConfirmDeleteNote(null)}>
                {print_lang('cancel', 'Cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteNote}>
                {print_lang('delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientRecord;
